<script setup lang="ts">
const { contactInfo } = useContact()

const { getAllFAQ, getCategories } = useFAQ()

const activeCategory = ref('Tous')
const categories = getCategories()
const openIndex = ref<number | null>(null)

const { data: allFaqs } = await useAsyncData('all-faqs', () => getAllFAQ())

const filteredFAQ = computed(() => {
  const faqs = allFaqs.value || []
  if (activeCategory.value === 'Tous') return faqs
  return faqs.filter(item => item.category === activeCategory.value)
})

const toggle = (index: number) => {
  openIndex.value = openIndex.value === index ? null : index
}

const setCategory = (category: string) => {
  activeCategory.value = category
  openIndex.value = null // Close all when switching category
}

const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'FAQ - Questions Fréquentes',
  description: 'Toutes les réponses à vos questions sur l\'école OXO : admission, pédagogie, vie étudiante et financement.'
})
</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <UiPageHero 
      pageKey="faq"
      badge-text="Support & FAQ"
      title-prefix="TOUT"
      title-highlight="S'ÉCLAIRCIT"
      description="Nous répondons ici à toutes vos interrogations sur la pédagogie, la vie à l'école et les admissions."
      :tags="['ADMISSION', 'PÉDAGOGIE', 'FINANCEMENT']"
      height="screen"
      align="left"
      :primary-button="{ text: 'POSER UNE QUESTION', to: '/contact', umamiEvent: 'FAQ - Hero - Contact' }"
      :secondary-button="{ text: 'DÉCOUVRIR L\'ÉCOLE', to: '/ecole', umamiEvent: 'FAQ - Hero - Ecole' }"
    />


    <section class="py-section-py">
      <div class="container mx-auto px-container-px max-w-4xl">
        
        <div class="flex flex-wrap justify-center mb-16">
          <UiTabs 
            :items="categories" 
            v-model="activeCategory"
          />
        </div>

        <!-- Accordion -->
        <div class="space-y-4">
          <div 
            v-for="(item, index) in filteredFAQ" 
            :key="index"
            class="group bg-oxo-card-bg rounded-xl border border-oxo-border overflow-hidden transition-all duration-300 shadow-xs dark:shadow-none hover:shadow-md"
          >
            <button 
              @click="toggle(index)"
              class="w-full flex items-center justify-between p-6 text-left focus:outline-hidden"
            >
              <span class="font-display font-bold text-lg text-foreground group-hover:text-oxo-red transition-colors">{{ item.question }}</span>
              <span 
                class="w-8 h-8 rounded-full bg-background/50 flex items-center justify-center text-foreground-dim transition-transform duration-300"
                :class="{ 'rotate-180 bg-oxo-red text-white': openIndex === index }"
              >
                <i class="fas fa-chevron-down text-sm" aria-hidden="true"></i>
              </span>
            </button>
            <div 
              v-show="openIndex === index"
              class="px-6 pb-6 text-foreground-dim leading-relaxed border-t border-oxo-border pt-4"
            >
              {{ item.answer }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-section-py bg-background/50 border-t border-oxo-border">
      <div class="container mx-auto px-container-px text-center">
        <h2 class="font-display font-bold text-3xl text-foreground mb-8">
          Vous n'avez pas trouvé votre réponse ?
        </h2>
        
        <div class="mb-12">
          <AppButton to="/contact" variant="secondary" size="lg">
            <i class="fas fa-envelope mr-2" aria-hidden="true"></i> Contactez-nous
          </AppButton>
        </div>

        <!-- Contact Info Grid -->
        <AppContactInfo />
      </div>
    </section>

    <AppCTA
      title="Envie de nous"
      titleHighlight="REJOINDRE ?"
      subtitle="Les inscriptions sont ouvertes. Lancez votre carrière dans le jeu vidéo dès maintenant."
    >
      <AppDiscoverLinks />
    </AppCTA>
  </div>
</template>

<style scoped>
</style>
